/**
 * PhoneVerification — confirmação de WhatsApp via Twilio Verify (OTP).
 * Fluxo: enviar código → digitar 6 dígitos → confirmado.
 */
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MessageCircle, CheckCircle2, Loader2, Pencil, ShieldCheck } from "lucide-react";
import { API } from "../App";

const localDigits = (raw) => {
  let d = (raw || "").replace(/\D+/g, "");
  if (d.startsWith("0055")) d = d.slice(4);
  else if (d.startsWith("55") && (d.length === 12 || d.length === 13)) d = d.slice(2);
  return d;
};

const maskBr = (raw) => {
  const d = localDigits(raw);
  if (d.length < 10) return null;
  return `+55 ${d.slice(0, 2)} •••••-${d.slice(-4)}`;
};

export const PhoneVerification = ({ phone, verified, onVerified, onChangeNumber, dark = false }) => {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  const masked = maskBr(phone);
  const phoneOk = localDigits(phone).length >= 10;

  useEffect(() => {
    setSent(false);
    setCode("");
  }, [phone]);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const startCountdown = () => {
    setCountdown(60);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(timerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const sendCode = async () => {
    if (!phoneOk) { toast.error("Informe um número de WhatsApp válido com DDD"); return; }
    setSending(true);
    try {
      const r = await fetch(`${API}/auth/phone/send-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Não foi possível enviar o código agora. Tente novamente em alguns instantes.");
      toast.success(d.message || "Código enviado pelo WhatsApp.");
      setSent(true);
      setCode("");
      startCountdown();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  const confirmCode = async () => {
    if (code.trim().length < 4) { toast.error("Digite o código recebido no WhatsApp"); return; }
    setChecking(true);
    try {
      const headers = { "Content-Type": "application/json" };
      const token = localStorage.getItem("token");
      if (token) headers.Authorization = `Bearer ${token}`;
      const r = await fetch(`${API}/auth/phone/check-code`, {
        method: "POST",
        headers,
        body: JSON.stringify({ phone, code: code.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Código inválido. Confira e tente novamente.");
      toast.success("Número verificado com sucesso!");
      onVerified?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setChecking(false);
    }
  };

  const boxCls = dark
    ? "rounded-xl border-2 border-emerald-500/30 bg-slate-800/60 p-4"
    : "rounded-xl border-2 border-emerald-200 bg-emerald-50/70 p-4";
  const titleCls = dark ? "text-white" : "text-gray-900";
  const textCls = dark ? "text-slate-400" : "text-gray-600";
  const inputCls = dark ? "bg-slate-800 border-slate-700 text-white" : "";

  if (verified) {
    return (
      <div className={boxCls} data-testid="phone-verify-status">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
          <div>
            <p className={`text-sm font-bold ${titleCls}`}>Número verificado com sucesso!</p>
            {masked && <p className={`text-xs ${textCls}`}>{masked}</p>}
          </div>
        </div>
      </div>
    );
  }

  if (!sent) {
    return (
      <div className={boxCls} data-testid="phone-verify-idle">
        <div className="flex items-start gap-2 mb-3">
          <MessageCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
          <div>
            <p className={`text-sm font-bold ${titleCls}`}>Confirme seu WhatsApp</p>
            <p className={`text-xs ${textCls}`}>Enviaremos um código de segurança para confirmar que este número pertence a você.</p>
            {masked && <p className={`text-xs font-mono mt-1 ${titleCls}`}>{masked}</p>}
          </div>
        </div>
        <Button
          type="button"
          onClick={sendCode}
          disabled={sending || !phoneOk}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg"
          data-testid="phone-verify-send-btn"
        >
          {sending
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando…</>
            : <><MessageCircle className="w-4 h-4 mr-2" />Enviar código pelo WhatsApp</>}
        </Button>
        {!phoneOk && (
          <p className={`text-[11px] mt-2 ${textCls}`}>Digite o número com DDD para habilitar o envio.</p>
        )}
      </div>
    );
  }

  return (
    <div className={boxCls} data-testid="phone-verify-code-step">
      <div className="flex items-start gap-2 mb-3">
        <ShieldCheck className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
        <div>
          <p className={`text-sm font-bold ${titleCls}`}>Digite o código</p>
          <p className={`text-xs ${textCls}`}>Enviamos um código de verificação para o WhatsApp informado.</p>
          {masked && <p className={`text-xs font-mono mt-1 ${titleCls}`}>{masked}</p>}
        </div>
      </div>
      <Input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={8}
        placeholder="Código de 6 dígitos"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D+/g, ""))}
        className={`text-center font-mono text-lg tracking-[0.4em] ${inputCls}`}
        data-testid="phone-verify-code-input"
      />
      <div className="grid grid-cols-1 gap-2 mt-3">
        <Button
          type="button"
          onClick={confirmCode}
          disabled={checking || code.length < 4}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg"
          data-testid="phone-verify-confirm-btn"
        >
          {checking
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Validando…</>
            : "Confirmar código"}
        </Button>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={sendCode}
            disabled={sending || countdown > 0}
            className={`flex-1 text-xs ${dark ? "border-slate-700 bg-slate-800 text-white hover:bg-slate-700" : ""}`}
            data-testid="phone-verify-resend-btn"
          >
            {countdown > 0 ? `Reenviar código (${countdown}s)` : "Reenviar código"}
          </Button>
          {onChangeNumber && (
            <Button
              type="button"
              variant="outline"
              onClick={() => { setSent(false); setCode(""); onChangeNumber(); }}
              className={`flex-1 text-xs ${dark ? "border-slate-700 bg-slate-800 text-white hover:bg-slate-700" : ""}`}
              data-testid="phone-verify-change-btn"
            >
              <Pencil className="w-3 h-3 mr-1" />Alterar número
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
